import React, { useState, useEffect } from 'react'
import { isAfter, differenceInMinutes } from 'date-fns'
import { Button, Modal } from 'antd'
import { CloseCircleOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { usePostHog } from 'posthog-js/react'

import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { DASHBOARD_SURVEY_STORAGE_KEY } from '../../types/dashboard'
import { TrialSurveyForm } from './trial-survey-form'
import { TrialSurveySuccess } from './trial-survey-success'
import { MsFeedbackSuccess } from './ms-feedback-success'
import { SurveyIntro } from './survey-intro'
import { SurveyFooter } from './survey-footer'
import { ISurveyVariantConfig, SURVEY_VARIANTS } from './survey-variants'

export const SURVEY_TRIAL_EXTEND = 'surveyTrialExtend'

interface IPopupPresetProps {
  surveyLocalStorageKey: string
  showSurvey?: boolean
  setShowSurvey?: (v: boolean) => void
  timeOffsetMinutes?: number
  remindMeLaterTimeOffset?: number
}

interface IWorkspaceTrialSurveyProps {
  isMicrosoftBilled: boolean
  showSurvey?: boolean
  setShowSurvey?: (v: boolean) => void
  timeOffsetMinutes?: number
  remindMeLaterTimeOffset?: number
}

type SubmissionResult = { trialEnd?: string; showCalendlyCta?: boolean }

interface ITrialSurveyPopupProps extends IPopupPresetProps {
  config: ISurveyVariantConfig
  // role admin + trialing (the "when it may show" gate; variant-specific)
  isEligible: boolean
  // the survey is already completed for this workspace (e.g. extend coupon set)
  isAlreadyDone: boolean
  renderIntro?: () => React.ReactNode
  renderFooter?: () => React.ReactNode
  renderSuccess: (result: SubmissionResult, onClose: () => void) => React.ReactNode
}

interface IGetTimeDiffArgs {
  dateToCompare: Date
  surveyLocalStorageKey: string
  userId: string
}

interface IShouldShowSurveyArgs {
  isSurveyDone: boolean
  timeDiff: number
  surveyLocalStorageKey: string
  isRemindMeLaterClicked: boolean
  isAlreadyDone: boolean
  userId: string
  showSurvey?: boolean
  timeOffsetMinutes?: number
  remindMeLaterTimeOffset?: number
}

interface ISetDashboardSurveyInStorage {
  id: string
  isDone: boolean
  time?: Date
  remindMeLaterTime?: Date
  isRemindMeLaterClicked?: boolean
}

interface IDashboardSurveyStorage {
  id?: string
  isDone?: boolean
  time?: string
  remindMeLaterTime?: string
  isRemindMeLaterClicked?: boolean
}

const setDashboardSurveyInStorage = (data: ISetDashboardSurveyInStorage, surveyLocalStorageKey: string): void => {
  localStorage.setItem(surveyLocalStorageKey, JSON.stringify(data))
}

const parseSurveyStorage = (surveyLocalStorageKey: string): IDashboardSurveyStorage | null => {
  try {
    return JSON.parse(localStorage.getItem(surveyLocalStorageKey) as string)
  } catch {
    return null
  }
}

const getTimeDiff = ({ dateToCompare, surveyLocalStorageKey, userId }: IGetTimeDiffArgs): number => {
  const dashboardSurveyStorage = parseSurveyStorage(surveyLocalStorageKey)
  const surveyTime = dashboardSurveyStorage?.time ? new Date(dashboardSurveyStorage.time) : null
  const now = new Date()
  const companySignUpDate = new Date(dateToCompare)

  if (!surveyTime || !isAfter(surveyTime, companySignUpDate)) {
    setDashboardSurveyInStorage(
      {
        isDone: false,
        id: userId,
        time: companySignUpDate,
        remindMeLaterTime: new Date(),
        isRemindMeLaterClicked: false,
      },
      surveyLocalStorageKey
    )
    return Math.abs(Number(differenceInMinutes(companySignUpDate, now)))
  }

  return Math.abs(Number(differenceInMinutes(surveyTime, now)))
}

const shouldShowSurvey = ({
  isSurveyDone,
  timeOffsetMinutes,
  timeDiff,
  remindMeLaterTimeOffset,
  surveyLocalStorageKey,
  isRemindMeLaterClicked,
  showSurvey,
  isAlreadyDone,
  userId,
}: IShouldShowSurveyArgs): boolean => {
  if (isAlreadyDone) {
    setDashboardSurveyInStorage(
      {
        isDone: true,
        id: userId,
        time: new Date(),
        isRemindMeLaterClicked: false,
        remindMeLaterTime: new Date(),
      },
      surveyLocalStorageKey
    )
    return false
  }
  if (showSurvey) {
    return showSurvey
  }
  if (remindMeLaterTimeOffset && isRemindMeLaterClicked) {
    const now = new Date()
    const dateToCompare = new Date(parseSurveyStorage(surveyLocalStorageKey)?.remindMeLaterTime as string)
    const remindMeLaterTimeDiff = Math.abs(Number(differenceInMinutes(dateToCompare, now)))
    return !isSurveyDone && remindMeLaterTimeDiff >= remindMeLaterTimeOffset
  }

  if (timeOffsetMinutes && timeOffsetMinutes > 0) {
    return !isSurveyDone && timeDiff >= timeOffsetMinutes
  } else {
    return !isSurveyDone
  }
}

// Shared, config-driven survey modal. The "when to show it" machinery (12h/48h
// timing, localStorage, deep link, started/abandoned events) is identical across
// surveys; the config + render props supply the per-variant content.
function TrialSurveyPopup({
  config,
  surveyLocalStorageKey,
  timeOffsetMinutes,
  remindMeLaterTimeOffset,
  showSurvey,
  setShowSurvey,
  isEligible,
  isAlreadyDone,
  renderIntro,
  renderFooter,
  renderSuccess,
}: ITrialSurveyPopupProps): JSX.Element {
  const { formatMessage } = useIntl()
  const posthog = usePostHog()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [isSurveyDone, setIsSurveyDone] = useState(Boolean(parseSurveyStorage(surveyLocalStorageKey)?.isDone))
  const [showSurveyModal, setShowSurveyModal] = useState(false)
  const [isRemindMeLaterClicked, setIsRemindMeLaterClicked] = useState(Boolean(parseSurveyStorage(surveyLocalStorageKey)?.isRemindMeLaterClicked))
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null)
  const startedFiredRef = React.useRef(false)

  const fireStartedEvent = () => {
    if (startedFiredRef.current) return
    startedFiredRef.current = true
    posthog?.capture(config.events.started, {
      workspace_id: authCompany?.id,
      survey_version: 1,
    })
  }

  const handleCancel = () => {
    // Fire on any close-without-submit. Guard only on submissionResult (don't
    // log "abandoned" after a successful submit); isSurveyDone must NOT gate it,
    // or a force-opened/already-completed survey never logs the event.
    if (!submissionResult) {
      posthog?.capture(config.events.abandoned, {
        workspace_id: authCompany?.id,
        survey_version: 1,
      })
      setDashboardSurveyInStorage(
        {
          id: authUser?.id,
          isDone: false,
          time: new Date(),
          isRemindMeLaterClicked: false,
          remindMeLaterTime: new Date(),
        },
        surveyLocalStorageKey
      )
    }
    setIsRemindMeLaterClicked(false)
    setShowSurveyModal(false)
    setShowSurvey && setShowSurvey(false)
    startedFiredRef.current = false
    if (submissionResult) {
      setIsSurveyDone(true)
    } else {
      setIsSurveyDone(false)
    }
  }

  const handleRemindMeLater = () => {
    if (!submissionResult) {
      posthog?.capture(config.events.remindMeLater, {
        workspace_id: authCompany?.id,
        survey_version: 1,
        reason: 'remind_me_later',
      })
    }
    setDashboardSurveyInStorage(
      {
        id: authUser?.id,
        isDone: false,
        time: new Date(),
        isRemindMeLaterClicked: true,
        remindMeLaterTime: new Date(),
      },
      surveyLocalStorageKey
    )
    setShowSurveyModal(false)
    setShowSurvey && setShowSurvey(false)
    setIsSurveyDone(false)
    startedFiredRef.current = false
  }

  const handleSubmitted = (result: SubmissionResult) => {
    setSubmissionResult(result)
    setDashboardSurveyInStorage(
      {
        id: authUser.id,
        isDone: true,
        time: new Date(),
        isRemindMeLaterClicked: false,
        remindMeLaterTime: new Date(),
      },
      surveyLocalStorageKey
    )
    setIsSurveyDone(true)
  }

  useEffect(() => {
    if (isEligible && authCompany?.timestamp) {
      const timeDiff = getTimeDiff({
        dateToCompare: new Date(authCompany.timestamp as string),
        surveyLocalStorageKey,
        userId: authUser.id,
      })
      if (
        shouldShowSurvey({
          isSurveyDone,
          timeOffsetMinutes,
          timeDiff,
          remindMeLaterTimeOffset,
          surveyLocalStorageKey,
          isRemindMeLaterClicked,
          showSurvey,
          isAlreadyDone,
          userId: authUser.id,
        })
      ) {
        setShowSurveyModal(true)
        fireStartedEvent()
      }
    }
  }, [showSurvey, isEligible, isAlreadyDone])

  useEffect(() => {
    if (showSurvey && !isAlreadyDone) {
      setShowSurveyModal(showSurvey)
      localStorage.removeItem(surveyLocalStorageKey)
    }
  }, [showSurvey, isAlreadyDone])

  return (
    <Modal
      open={showSurveyModal}
      style={{ top: 20 }}
      closeIcon={<CloseCircleOutlined />}
      onCancel={handleCancel}
      footer={null}
      title={
        !submissionResult ? (
          <Button type="primary" onClick={handleRemindMeLater}>
            {formatMessage({ id: 'app.askMeLater' })}
          </Button>
        ) : null
      }
    >
      <div>
        {submissionResult ? (
          renderSuccess(submissionResult, handleCancel)
        ) : (
          <>
            {renderIntro?.()}
            <TrialSurveyForm config={config} onSubmitted={handleSubmitted} />
            {renderFooter?.()}
          </>
        )}
      </div>
    </Modal>
  )
}

const ExtendTrialPopUpSurvey = (props: IPopupPresetProps): JSX.Element => {
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const isEligible = authCompany?.subscriptionStatus === 'trialing' && authUser.role === 'Admin'
  const isAlreadyDone = Boolean(authCompany?.billing?.couponCodes?.includes(SURVEY_TRIAL_EXTEND))

  return (
    <TrialSurveyPopup
      {...props}
      config={SURVEY_VARIANTS.extendTrial}
      isEligible={isEligible}
      isAlreadyDone={isAlreadyDone}
      renderIntro={() => <SurveyIntro i18nPrefix="survey.extendTrial" />}
      renderFooter={() => <SurveyFooter />}
      renderSuccess={(result, onClose) => <TrialSurveySuccess trialEnd={result.trialEnd as string} onClose={onClose} />}
    />
  )
}

const MsTrialFeedbackPopup = (props: IPopupPresetProps): JSX.Element => {
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const isEligible = authCompany?.trialPeriod === 'in_trial' && authUser.role === 'Admin'

  return (
    <TrialSurveyPopup
      {...props}
      config={SURVEY_VARIANTS.msTrialFeedback}
      isEligible={isEligible}
      isAlreadyDone={false}
      renderIntro={() => <SurveyIntro i18nPrefix="survey.msTrialFeedback" />}
      renderSuccess={(result, onClose) => <MsFeedbackSuccess showCalendlyCta={Boolean(result.showCalendlyCta)} onClose={onClose} />}
    />
  )
}

// Single entry point: owns the survey's storage key and renders the variant the
// caller selects via isMicrosoftBilled. The caller controls whether to
// force-show it and the show timing.
function WorkspaceTrialSurvey({ isMicrosoftBilled, showSurvey, setShowSurvey, timeOffsetMinutes, remindMeLaterTimeOffset }: IWorkspaceTrialSurveyProps): JSX.Element {
  const presetProps: IPopupPresetProps = {
    surveyLocalStorageKey: DASHBOARD_SURVEY_STORAGE_KEY,
    timeOffsetMinutes,
    remindMeLaterTimeOffset,
    showSurvey,
    setShowSurvey,
  }

  return isMicrosoftBilled ? <MsTrialFeedbackPopup {...presetProps} /> : <ExtendTrialPopUpSurvey {...presetProps} />
}

export default WorkspaceTrialSurvey
